import React, { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle, Copy, Sheet, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { formatDateTime } from "@/lib/timezone";

/** Google Sheets reporting sync — Super Admin only.
 *
 * Flow for the user:
 * 1. Pre-create a spreadsheet in their own Google Drive
 * 2. Share it with the platform's Service Account email (shown here)
 * 3. Paste the spreadsheet ID
 * 4. Click "Sync Now" → manual sync; nightly sync runs at 02:00 studio TZ.
 */
export const GoogleSheetsSettings = ({ canEdit }) => {
  const [config, setConfig] = useState(null);
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState([]);
  const { default_timezone } = useBrand();

  const refresh = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/google-sheets/config"),
        api.get("/google-sheets/logs?limit=10"),
      ]);
      setConfig(c.data);
      setSpreadsheetId(c.data?.spreadsheet_id || "");
      setLogs(l.data?.logs || []);
    } catch (e) {
      // Probably not admin — just hide
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!config) {
    return <div className="text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Loading…</div>;
  }

  const saEmail = config.service_account_email;
  const lastSync = config.last_sync;

  const saveSpreadsheetId = async () => {
    setBusy(true);
    try {
      await api.patch("/google-sheets/config", { spreadsheet_id: spreadsheetId.trim() });
      toast.success("Spreadsheet ID saved");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const triggerSync = async () => {
    if (!spreadsheetId.trim()) { toast.error("Save a spreadsheet ID first"); return; }
    setSyncing(true);
    const startedAt = Date.now();
    try {
      // Sheets sync can take 30-90s on production-scale data (multiple mappers
      // + Google Sheets API retries). Override the default 30s axios timeout
      // so the request waits for the real outcome instead of giving up early.
      const r = await api.post("/google-sheets/sync", null, { timeout: 180_000 });
      const sheets = r.data?.result?.sheets || [];
      const total = sheets.reduce((a, s) => a + (s.rows || 0), 0);
      toast.success(`Synced ${total} rows across ${sheets.length} sheets in ${(r.data?.result?.duration_ms || 0) / 1000}s`);
      refresh();
    } catch (e) {
      // Some proxies / browsers still cut long requests even with a higher
      // timeout. In that case the backend usually finishes the sync after the
      // socket is dropped. Poll the log endpoint for the actual outcome
      // before showing a hard error.
      const wasTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      if (wasTimeout) {
        toast.message("Sync is still running — waiting for it to finish…", { duration: 3000 });
        const settled = await pollForSyncCompletion(startedAt);
        if (settled?.status === "success") {
          const sheets = settled?.summary?.sheets || [];
          const total = sheets.reduce((a, s) => a + (s.rows || 0), 0);
          toast.success(`Synced ${total} rows across ${sheets.length} sheets in ${(settled.duration_ms || 0) / 1000}s`);
        } else if (settled?.status === "failed") {
          toast.error(`Sync failed: ${settled.error || "unknown error"}`);
        } else {
          toast.message("Sync is taking longer than usual", {
            description: "Check the Recent syncs panel in a minute for the final outcome.",
          });
        }
      } else {
        toast.error(e.response?.data?.detail || "Sync failed");
      }
      refresh();
    } finally { setSyncing(false); }
  };

  /**
   * After a timeout, the backend usually finishes the in-flight sync. Polls
   * `/api/google-sheets/logs` every 5s for up to 2 minutes, looking for a log
   * row whose `started_at >= startedAt` and whose `status` is no longer
   * "running". Returns the matching log row (or null on timeout).
   */
  const pollForSyncCompletion = async (startedAt) => {
    const deadline = Date.now() + 120_000; // 2 min budget
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 5_000));
      try {
        const r = await api.get("/google-sheets/logs?limit=5");
        const rows = r.data?.logs || [];
        const ours = rows.find((row) => {
          if (!row?.started_at) return false;
          const rowMs = new Date(row.started_at).getTime();
          // Allow 10s clock skew between server and browser.
          return rowMs >= (startedAt - 10_000);
        });
        if (ours && ours.status && ours.status !== "running") {
          return ours;
        }
      } catch {
        // ignore transient errors, keep polling
      }
    }
    return null;
  };

  const copySaEmail = async () => {
    try { await navigator.clipboard.writeText(saEmail || ""); toast.success("Service account email copied"); } catch (_) { /* */ }
  };

  return (
    <div className="space-y-5" data-testid="gsheets-settings">
      {/* Service account email block — needed for the user to share their sheet */}
      <div className="p-4 rounded-2xl bg-violet-50/60 border border-violet-200">
        <div className="flex items-center gap-2 mb-2">
          <Sheet className="w-4 h-4 text-violet-600" />
          <h4 className="text-sm font-semibold text-slate-900">Service Account</h4>
        </div>
        {saEmail ? (
          <>
            <p className="text-xs text-slate-600 mb-2">
              <strong>Step 1:</strong> Share your Google spreadsheet with this email (Editor permission).
            </p>
            <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2">
              <code className="text-xs font-mono text-violet-700 break-all flex-1" data-testid="gsheets-sa-email">{saEmail}</code>
              <Button size="sm" variant="ghost" onClick={copySaEmail} className="h-7 px-2 shrink-0" data-testid="gsheets-copy-email">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <div>
              <p className="font-semibold">Service account not configured</p>
              <p>
                Set <code className="font-mono">GOOGLE_SHEETS_CREDENTIALS_JSON</code> in <code className="font-mono">backend/.env</code> with the
                full Service Account JSON contents (single line). Restart the backend after saving.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Spreadsheet ID input */}
      <div className="p-4 rounded-2xl border border-slate-200">
        <Label className="text-xs font-semibold text-slate-700">Spreadsheet ID</Label>
        <p className="text-xs text-slate-500 mt-1 mb-2">
          <strong>Step 2:</strong> Open your spreadsheet → copy the ID from the URL between <code className="font-mono">/d/</code> and{" "}
          <code className="font-mono">/edit</code>.
        </p>
        <div className="flex gap-2">
          <Input
            data-testid="gsheets-spreadsheet-input"
            value={spreadsheetId}
            onChange={(e) => setSpreadsheetId(e.target.value)}
            placeholder="1A2bC3dE4fG5hI6jK7lM..."
            disabled={!canEdit || busy}
            className="rounded-full text-sm"
          />
          <Button
            onClick={saveSpreadsheetId}
            disabled={!canEdit || busy || spreadsheetId.trim() === (config.spreadsheet_id || "").trim()}
            className="rounded-full bg-violet-600 hover:bg-violet-700 text-white shrink-0"
            data-testid="gsheets-save-id"
          >
            Save
          </Button>
        </div>
        {config.spreadsheet_id && (
          <a
            href={`https://docs.google.com/spreadsheets/d/${config.spreadsheet_id}/edit`}
            target="_blank" rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs text-violet-700 hover:underline"
            data-testid="gsheets-open-link"
          >
            Open spreadsheet <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Sync */}
      <div className="p-4 rounded-2xl border border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Sync</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Manual sync rebuilds raw sheet data. Nightly auto-sync runs at <strong>02:00 {default_timezone}</strong>.
            </p>
          </div>
          <Button
            onClick={triggerSync}
            disabled={syncing || !config.spreadsheet_id}
            className="rounded-full bg-green-600 hover:bg-green-700 text-white"
            data-testid="gsheets-sync-now"
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        {lastSync && (
          <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs flex items-start gap-2" data-testid="gsheets-last-sync">
            {lastSync.status === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
            ) : lastSync.status === "running" ? (
              <Loader2 className="w-4 h-4 text-violet-600 shrink-0 mt-0.5 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-700">
                Last sync: {lastSync.status?.toUpperCase()}
                <span className="text-slate-400 font-normal ml-1">·</span>{" "}
                <span className="text-slate-500 font-normal">{formatDateTime(lastSync.started_at, default_timezone)}</span>
              </p>
              {lastSync.summary?.sheets?.length ? (
                <p className="text-slate-500 mt-0.5">
                  {lastSync.summary.sheets.map((s) => `${s.sheet}: ${s.rows} rows`).join(" · ")}
                  {lastSync.duration_ms ? ` · ${(lastSync.duration_ms / 1000).toFixed(1)}s` : ""}
                </p>
              ) : null}
              {lastSync.error && <p className="text-red-600 mt-0.5 break-words">{lastSync.error}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Recent logs */}
      {logs.length > 0 && (
        <div className="p-4 rounded-2xl border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-900">Recent syncs</h4>
          </div>
          <div className="space-y-1.5">
            {logs.map((l) => {
              // A row stuck in "running" for >5 min almost certainly belongs
              // to a sync whose connection died — surface that to the admin
              // so they don't mistake it for an in-flight job.
              const isStaleRunning =
                l.status === "running" &&
                l.started_at &&
                (Date.now() - new Date(l.started_at).getTime()) > 5 * 60 * 1000;
              const displayStatus = isStaleRunning ? "STALE" : (l.status || "").toUpperCase();
              const pillClass = isStaleRunning
                ? "bg-amber-100 text-amber-700"
                : l.status === "success" ? "bg-green-100 text-green-700"
                : l.status === "running" ? "bg-violet-100 text-violet-700"
                : "bg-red-100 text-red-700";
              return (
                <div key={l.sync_id} className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${pillClass}`}>
                      {displayStatus}
                    </span>
                    <span className="text-slate-500 truncate">{formatDateTime(l.started_at, default_timezone)}</span>
                    {isStaleRunning && (
                      <span className="text-amber-600 text-[10px] italic" title="No completion log — the sync connection likely dropped. The actual sheet may still have been updated.">
                        (connection dropped)
                      </span>
                    )}
                  </div>
                  <span className="text-slate-400 text-[10px] shrink-0 capitalize">{l.triggered_by || "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleSheetsSettings;
